import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { i18_TRANSLATION } from "../../../i18nReferences";
import { getHelperText } from "../../../yupLocalization";

/**
 * Common component for deposit name input field with info popover
 * 
 * @param {Object} props
 * @param {Function} props.register - React Hook Form register function
 * @param {Object} props.errors - React Hook Form errors object
 * @param {string} props.fieldName - Name of the form field (default: "depositName")
 * @param {boolean} props.required - Whether the field is required (default: false)
 * @param {boolean} props.fullWidth - Whether the field should be full width (default: true)
 * @param {string} props.helperTextNamespace - Namespace for helper text translations (default: i18_TRANSLATION)
 */
const DepositNameInput = ({ 
    register, 
    errors, 
    fieldName = "depositName",
    required = false,
    fullWidth = true,
    helperTextNamespace = i18_TRANSLATION,
    slotProps,
    ...rest 
}) => {
    const { t } = useTranslation([i18_TRANSLATION]);
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);

    // Deposit name examples from i18n
    const depositExamples = t("PLACEHOLDER.DEPOSIT", { ns: i18_TRANSLATION });

    const handleInfoClick = (event) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };

    const infoOpen = Boolean(infoAnchorEl);

    return (
        <>
            <TextField
                required={required}
                {...register(fieldName)}
                fullWidth={fullWidth}
                error={!!errors?.[fieldName]}
                helperText={getHelperText(t, errors, fieldName, helperTextNamespace) || t("DEPOSIT_NAME_HELPER", { ns: i18_TRANSLATION })}
                variant="standard"
                label={t("DEPOSIT_NAME_LABEL", { ns: i18_TRANSLATION })}
                slotProps={{
                    input: {
                        maxLength: 30,
                        ...(slotProps?.input || {}),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleInfoClick}
                                    edge="end"
                                >
                                    <HelpOutlinedIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
                {...rest}
            />
            <Popover
                open={infoOpen}
                anchorEl={infoAnchorEl}
                onClose={handleInfoClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Box sx={{ p: 2, maxWidth: 350 }}>
                    <Typography variant="body2" gutterBottom>
                        {t("DEPOSIT_NAME_INFO", { ns: i18_TRANSLATION })}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                        {depositExamples}
                    </Typography>
                </Box>
            </Popover>
        </>
    );
};

export default DepositNameInput;

